/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import gql from 'graphql-tag';
import { compact } from 'lodash';
import { compose } from 'recompose';

import { Typography } from '@coursera/cds-core';

import {
  buildCertificatesUrlRelative,
  buildDegreeUrlAbsolute,
  buildMastertrackUrlAbsolute,
  buildProfessionalCertificateUrlAbsolute,
} from 'bundles/common/utils/urlUtils';
import DegreeOfferingCard from 'bundles/degrees/components/DegreeOfferingCard';
import withFragments from 'bundles/graphql/components/withFragments';
import type {
  PartnerPageQuery_PartnersV1Resource_shortName_partners_mastertracks as Mastertrack,
  PartnerPageQuery_PartnersV1Resource_shortName_partners_professionalCertificates as ProfessionalCert,
} from 'bundles/partners-graphql/components/__generated__/PartnerPageQuery';
import { masterTrackVariants } from 'bundles/partners-graphql/constants';
import type { PartnersPremiumProductCollections_LearningProductFragment } from 'bundles/partners-graphql/queries/__generated__/GetAllDegrees';
import { AVAILABILITY_STATUS } from 'bundles/premium-hub/types';

import _t from 'i18n!nls/partners-graphql';

const styles = {
  degreesUpsell: css`
    padding: 15px;
  `,
  degreesUpsellHeader: css`
    margin-bottom: 16px !important;
  `,
};

type Props = {
  degrees: PartnersPremiumProductCollections_LearningProductFragment[] | null;
  mastertracks: Mastertrack | null;
  professionalCertificates: ProfessionalCert | null;
};

export const DegreesUpsell = ({ degrees, mastertracks, professionalCertificates }: Props) => {
  const availableMastertracks = mastertracks?.elements.filter(
    (mastertrack) => mastertrack?.catalogAvailabilityStatus !== AVAILABILITY_STATUS.ARCHIVED
  );

  const availableDegrees =
    degrees?.filter((degree) => degree?.catalogAvailabilityStatus !== AVAILABILITY_STATUS.ARCHIVED) ?? [];

  const degreeOfferings = availableDegrees?.map((degree) => {
    if (!degree || !degree.thumbnailUrl) {
      return null;
    }

    return {
      id: degree.id,
      name: degree.name,
      subtitle: degree.tagline,
      iconSrc: degree.thumbnailUrl,
      link: buildDegreeUrlAbsolute(degree.slug),
    };
  });
  const mastertrackOfferings = availableMastertracks?.map((mastertrack) => {
    if (!mastertrack || !mastertrack.thumbnail) {
      return null;
    }

    // Other certificate programs are returned in this response, but all the other
    // ones use /certificate in their links
    const programLink =
      mastertrack.productVariant === masterTrackVariants.Mastertrack
        ? buildMastertrackUrlAbsolute(mastertrack.slug)
        : buildCertificatesUrlRelative(mastertrack.slug);

    return {
      id: mastertrack.id,
      name: mastertrack.name,
      subtitle: mastertrack.tagline,
      iconSrc: mastertrack.thumbnail,
      link: programLink,
    };
  });

  const pcOfferings = professionalCertificates?.elements.map((pc) => {
    if (!pc || !pc.thumbnail) {
      return null;
    }

    return {
      id: pc.id,
      name: pc.name,
      subtitle: pc.tagline,
      iconSrc: pc.thumbnail,
      link: buildProfessionalCertificateUrlAbsolute(pc.slug),
    };
  });
  const allOfferings = [...compact(degreeOfferings), ...compact(mastertrackOfferings), ...compact(pcOfferings)];

  if (allOfferings.length === 0) return null;

  return (
    <div className="rc-DegreesUpsell" css={styles.degreesUpsell}>
      <Typography css={styles.degreesUpsellHeader} variant="h2" component="h3">
        {_t('Degrees + Certificates')}
      </Typography>
      {allOfferings.map((offering) => (
        <DegreeOfferingCard offering={offering} key={offering.id} />
      ))}
    </div>
  );
};

export const degreesUpsellMastertracksFragment = gql`
  fragment MastertracksFragment on CatalogMastertracksV1Connection {
    elements {
      id
      slug
      name
      productVariant
      tagline
      thumbnail
      catalogAvailabilityStatus
    }
  }
`;

export const degreesUpsellProfessionalCertificatesFragment = gql`
  fragment ProfessionalCertificates on ProfessionalCertsV1Connection {
    elements {
      id
      slug
      name
      tagline
      thumbnail
    }
  }
`;

export default compose<Props, Props>(
  withFragments({
    mastertracks: degreesUpsellMastertracksFragment,
    professionalCertificates: degreesUpsellProfessionalCertificatesFragment,
  })
)(DegreesUpsell);
